import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { Checkbox } from '@/components/common/Checkbox';
import { useFilters } from '@/context/FiltersContext';
import { AMENITY_LABELS } from '@/constants';

interface AmenityFilterProps {
  className?: string;
  stations?: Array<{ amenities: string[] }>;
}

// Known amenities that we display in the filter
const KNOWN_AMENITIES = [
  'car_wash',
  'customer_toilets',
  'water_filling',
  'adblue_packaged',
  'adblue_pumps',
];

export function AmenityFilter({ className, stations = [] }: AmenityFilterProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { selectedAmenities, toggleAmenity } = useFilters();

  // Count stations that have ALL selected amenities (AND logic)
  const matchingCount = useMemo(() => {
    if (selectedAmenities.length === 0) return stations.length;

    return stations.filter((station) =>
      selectedAmenities.every((amenity) => station.amenities.includes(amenity))
    ).length;
  }, [stations, selectedAmenities]);

  // Count how many stations have each amenity
  const amenityCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const amenity of KNOWN_AMENITIES) {
      const count = stations.filter((s) => s.amenities.includes(amenity)).length;
      counts.set(amenity, count);
    }
    return counts;
  }, [stations]);

  const hasStations = stations.length > 0;
  const filterActive = selectedAmenities.length > 0;

  return (
    <div className={className}>
      {/* Header with expand toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-1 group"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-700">Filter by Amenities</h3>
          {filterActive && (
            <span className="px-1.5 py-0.5 bg-brand-100 text-brand-700 text-xs font-medium rounded">
              {selectedAmenities.length}
            </span>
          )}
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <svg
            className="w-4 h-4 text-gray-400 group-hover:text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>

      {/* Expandable content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3 space-y-3">
              {/* Matching count */}
              {hasStations && filterActive && (
                <p className="text-xs text-gray-500">
                  {matchingCount} station{matchingCount !== 1 ? 's' : ''} match{matchingCount === 1 ? 'es' : ''}
                </p>
              )}

              {/* Amenity checkboxes */}
              <div className="space-y-1.5">
                {KNOWN_AMENITIES.map((amenity) => {
                  const count = amenityCounts.get(amenity) || 0;
                  const isDisabled = !hasStations || count === 0;

                  return (
                    <Checkbox
                      key={amenity}
                      checked={selectedAmenities.includes(amenity)}
                      onChange={() => toggleAmenity(amenity)}
                      disabled={isDisabled}
                      label={
                        <span
                          className={clsx(
                            'flex items-center justify-between w-full',
                            isDisabled && 'text-gray-400'
                          )}
                        >
                          <span className="flex items-center gap-2">
                            <AmenityIcon amenity={amenity} className="w-4 h-4" />
                            <span>{AMENITY_LABELS[amenity] || amenity}</span>
                          </span>
                          {hasStations && (
                            <span className="text-xs text-gray-400 ml-2">({count})</span>
                          )}
                        </span>
                      }
                    />
                  );
                })}
              </div>

              <p className="text-xs text-gray-500 italic">
                Stations must have all selected amenities
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Simple icon component for amenities
function AmenityIcon({ amenity, className }: { amenity: string; className?: string }) {
  const icons: Record<string, React.ReactNode> = {
    car_wash: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
      </svg>
    ),
    customer_toilets: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    water_filling: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
    adblue_packaged: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    adblue_pumps: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  };

  return icons[amenity] || (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
